'use client';

import { type ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import { Confetti, HandHeart, Heart, Star } from '@phosphor-icons/react';
import type { ReactionKind } from '@leanin/shared/reactions';
import { CircleStack, Icon } from '@leanin/ui';
import { ToggleGroupPrimitive } from '@leanin/ui/client';
import styles from './ReactionPicker.module.css';

const reactionIconProps = { size: '100%', weight: 'fill' } as const;

export type { ReactionKind };

const ICON_TO_DISC_RATIO = 0.62;

export const REACTIONS: ReadonlyArray<{
  id: ReactionKind;
  label: string;
  icon: ReactNode;
  fill: string;
  ink: string;
}> = [
  { id: 'support',    label: 'Love',      icon: <Heart     {...reactionIconProps} />, fill: '#ffe4e6', ink: '#be123c' },
  { id: 'helpful',    label: 'Celebrate', icon: <Confetti  {...reactionIconProps} />, fill: '#ffedd5', ink: '#ea580c' },
  { id: 'insightful', label: 'Helpful',   icon: <Star      {...reactionIconProps} />, fill: '#fef3c7', ink: '#ca8a04' },
  { id: 'same',       label: 'Care',      icon: <HandHeart {...reactionIconProps} />, fill: '#ede9fe', ink: '#7c3aed' },
];

export const REACTION_BY_ID = Object.fromEntries(
  REACTIONS.map((r) => [r.id, r]),
) as Record<ReactionKind, (typeof REACTIONS)[number]>;

// The kind a plain click applies — the picker handles the other three. Shared
// so the glyph an un-reacted chip previews cannot drift from what pressing
// that chip actually does.
export const DEFAULT_REACTION: ReactionKind = 'support';

export const REACTION_LABEL_KEY = {
  support: 'reactions.love',
  helpful: 'reactions.celebrate',
  insightful: 'reactions.helpful',
  same: 'reactions.care',
} as const satisfies Record<ReactionKind, string>;

function topReactions(
  counts: Record<ReactionKind, number>,
  mine: ReactionKind | null | undefined,
  limit: number,
): ReadonlyArray<(typeof REACTIONS)[number]> {
  const order = new Map(REACTIONS.map((r, i) => [r.id, i]));
  return REACTIONS
    .filter((r) => counts[r.id] > 0)
    .sort((a, b) => {
      if (a.id === mine) return -1;
      if (b.id === mine) return 1;
      const cmp = counts[b.id] - counts[a.id];
      return cmp !== 0 ? cmp : (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0);
    })
    .slice(0, limit);
}

export function ReactionDisc({
  reaction,
  size,
  active = false,
}: {
  reaction: (typeof REACTIONS)[number];
  size: number;
  /** When true, swap fill↔ink for a "selected" inverted appearance. */
  active?: boolean;
}) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--radius-full)',
        background: active ? reaction.ink : reaction.fill,
        color: active ? reaction.fill : reaction.ink,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={Math.round(size * ICON_TO_DISC_RATIO)}>
        {reaction.icon}
      </Icon>
    </span>
  );
}

// The disc an un-reacted chip shows: the reaction a click would apply, in
// that reaction's own colours. The chip around it carries the un-reacted
// state (untinted, no coloured border) — the glyph stays in colour rather
// than greying out with it, so the affordance reads as available instead of
// disabled.
export function DefaultReactionDisc({ size = 18 }: { size?: number }) {
  return (
    <ReactionDisc reaction={REACTION_BY_ID[DEFAULT_REACTION]} size={size} />
  );
}

export function ReactionStack({
  counts,
  mine,
  size = 22,
  max = 3,
}: {
  counts: Record<ReactionKind, number>;
  mine: ReactionKind | null | undefined;
  size?: number;
  max?: number;
}) {
  const items = topReactions(counts, mine, max);
  if (items.length === 0) return null;
  return (
    <CircleStack
      items={items}
      size={size}
      renderItem={(r) => (
        <ReactionDisc reaction={r} size={size} active={r.id === mine} />
      )}
    />
  );
}

export function ReactionPicker({
  mine,
  onPick,
}: {
  mine: ReactionKind | null | undefined;
  onPick: (kind: ReactionKind) => void;
}) {
  const t = useTranslations('Feed');
  return (
    <ToggleGroupPrimitive.Root
      type="single"
      // '' (not undefined) keeps the group controlled when nothing is
      // selected — undefined flips Radix to uncontrolled and back.
      value={mine ?? ''}
      onValueChange={(value: string) => {
        // Radix reports re-clicking the selected item as ''. That tap means
        // "remove my reaction" — forward the held kind and let the server
        // toggle delete it. This is the only unreact path now that the
        // summary pill's click opens the roster.
        if (value === '') {
          if (mine != null) onPick(mine);
          return;
        }
        onPick(value as ReactionKind);
      }}
      aria-label={t('reactions.pickerAria')}
      className={styles.root}
    >
      {REACTIONS.map((r) => (
        <ToggleGroupPrimitive.Item
          key={r.id}
          value={r.id}
          aria-label={t(REACTION_LABEL_KEY[r.id])}
          className={styles.item}
        >
          <ReactionDisc reaction={r} size={36} active={r.id === mine} />
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}
